import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Add auth header for admin routes
    if (config.url?.startsWith('/admin')) {
      // Use ID token for backend auth (has correct audience)
      // Access token is for Microsoft Graph API
      const token = localStorage.getItem('idToken') || localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('user');
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/auth/callback')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================
// PUBLIC CLIENT API
// ============================================

export const clientApi = {
  /**
   * Get public MSP configuration (branding)
   */
  async getConfig() {
    return api.get('/client/config');
  },

  /**
   * Create a new deposit
   */
  async createDeposit(data: FormData) {
    return api.post('/client/deposit', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Get a message by token
   */
  async getMessage(token: string) {
    return api.get(`/client/message/${token}`);
  },

  /**
   * Mark a message as read
   */
  async markAsRead(token: string) {
    return api.post(`/client/message/${token}/read`);
  },

  /**
   * Download an attachment
   */
  async downloadAttachment(token: string, attachmentId: string) {
    return api.get(`/client/message/${token}/download/${attachmentId}`, {
      responseType: 'blob',
    });
  },

  /**
   * Check if SMS verification is enabled
   */
  async getSmsStatus() {
    return api.get('/client/sms/status');
  },

  /**
   * Send SMS verification code (uses phone number stored in message)
   */
  async sendSmsCode(token: string) {
    return api.post(`/client/message/${token}/sms/send`);
  },

  /**
   * Verify SMS code
   */
  async verifySmsCode(token: string, code: string) {
    return api.post(`/client/message/${token}/sms/verify`, { code });
  },
};

// ============================================
// AUTH API
// ============================================

export const authApi = {
  /**
   * Get Azure AD configuration
   */
  async getConfig() {
    return api.get('/auth/config');
  },

  /**
   * Exchange authorization code for tokens (with PKCE)
   */
  async exchangeToken(code: string, codeVerifier: string) {
    return api.post('/auth/token', { code, codeVerifier });
  },

  /**
   * Development login
   */
  async devLogin(email?: string, name?: string) {
    return api.post('/auth/dev-login', { email, name });
  },

  /**
   * Logout
   */
  async logout() {
    return api.post('/auth/logout');
  },

  /**
   * Get current user
   */
  async getMe() {
    return api.get('/auth/me');
  },
};

// ============================================
// ADMIN API
// ============================================

export const adminApi = {
  // Dashboard
  async getDashboard() {
    return api.get('/admin/dashboard');
  },

  // Deposits
  async listDeposits(params?: { page?: number; limit?: number; status?: string; search?: string }) {
    return api.get('/admin/deposits', { params });
  },

  async getDeposit(id: string) {
    return api.get(`/admin/deposits/${id}`);
  },

  async deleteDeposit(id: string) {
    return api.delete(`/admin/deposits/${id}`);
  },

  async downloadDepositAttachment(depositId: string, attachmentId: string) {
    return api.get(`/admin/deposits/${depositId}/attachments/${attachmentId}`, {
      responseType: 'blob',
    });
  },

  // Messages
  async listMessages(params?: { page?: number; limit?: number; status?: string }) {
    return api.get('/admin/messages', { params });
  },

  async createMessage(data: {
    recipientName: string;
    recipientEmail?: string;
    recipientPhone?: string;
    content: string;
    expiresInHours?: number;
    maxReads?: number;
    notifyOnRead?: boolean;
    files?: File[];
  }) {
    const formData = new FormData();
    formData.append('recipientName', data.recipientName);
    if (data.recipientEmail) formData.append('recipientEmail', data.recipientEmail);
    if (data.recipientPhone) formData.append('recipientPhone', data.recipientPhone);
    formData.append('content', data.content);
    if (data.expiresInHours) formData.append('expiresInHours', data.expiresInHours.toString());
    if (data.maxReads) formData.append('maxReads', data.maxReads.toString());
    if (data.notifyOnRead !== undefined) formData.append('notifyOnRead', data.notifyOnRead.toString());
    data.files?.forEach((file) => formData.append('files', file));

    return api.post('/admin/messages', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  async getMessage(id: string) {
    return api.get(`/admin/messages/${id}`);
  },

  async deleteMessage(id: string) {
    return api.delete(`/admin/messages/${id}`);
  },

  // Config
  async getConfig() {
    return api.get('/admin/config');
  },

  async updateConfig(config: Record<string, unknown>) {
    return api.put('/admin/config', config);
  },

  // Audit logs
  async getAuditLogs(params?: {
    page?: number;
    limit?: number;
    eventType?: string;
    eventCategory?: string;
    startDate?: string;
    endDate?: string;
    mspUserId?: string;
    actionResult?: string;
  }) {
    return api.get('/admin/audit', { params });
  },

  async exportAuditLogs(startDate: string, endDate: string, format: string = 'json') {
    return api.get('/admin/audit/export', {
      params: { startDate, endDate, format },
      responseType: 'blob',
    });
  },

  async getAuditStats() {
    return api.get('/admin/audit/stats');
  },

  async getSecurityAlerts() {
    return api.get('/admin/audit/security-alerts');
  },

  // Email
  async sendEmail(data: {
    to: Array<{ email: string; name?: string }>;
    cc?: Array<{ email: string; name?: string }>;
    subject: string;
    htmlBody: string;
    textBody?: string;
    graphAccessToken: string;
  }) {
    return api.post('/admin/email/send', data);
  },

  async sendSecureLinkEmail(data: {
    recipientEmail: string;
    recipientName: string;
    messageUrl: string;
    expiresAt: string;
    customMessage?: string;
    graphAccessToken: string;
  }) {
    return api.post('/admin/email/send-secure-link', data);
  },
};

export default api;
